/**
 * Phase 5.8 (preview) — Design Theme Lab
 *
 * Public route at /design-preview. NON-DESTRUCTIVE: lets the owner compare 4
 * color treatments on a mock customer dashboard without touching any real code
 * or user state. All styling here is scoped to this file using CSS variables;
 * picking a winner becomes a 3-line CSS swap once approved.
 *
 * Themes:
 *   1. TERMINAL ACID  — current production baseline (green + magenta premium)
 *   2. STEVENSON      — acid green + electric magenta (from the tuning ad ref)
 *   3. OFFEND         — crimson red + acid yellow-green (from the "Live to Offend" ref)
 *   4. MIDNIGHT       — deep indigo bg + mint + amber (boutique / calmer)
 */
import { useState } from 'react';
import { Sparkles, Clock, ShoppingCart, Package, Bell, Gift, User, Plus, Heart, Search } from 'lucide-react';

const THEMES = {
  terminal: {
    name: 'TERMINAL ACID',
    tagline: 'Current production · pure coder-terminal',
    swatch: ['#050806', '#33FF57', '#FF3D8E'],
    vars: {
      '--bg':         '#050806',
      '--surface':    '#0A0F0C',
      '--border':     '#152E1D',
      '--primary':    '#33FF57',
      '--primary-dim':'#A3C7A8',
      '--text-mute':  '#4B6B52',
      '--accent':     '#FF3D8E',
      '--accent-warm':'#FFB000',
      '--orange':     '#FF9F0A',
    },
  },
  stevenson: {
    name: 'STEVENSON',
    tagline: 'Acid green + electric magenta · loud street vibe',
    swatch: ['#050003', '#39FF14', '#B400FF'],
    vars: {
      '--bg':         '#050003',
      '--surface':    '#0C0612',
      '--border':     '#2A0F3D',
      '--primary':    '#39FF14',
      '--primary-dim':'#B9F2A8',
      '--text-mute':  '#5C4470',
      '--accent':     '#B400FF',
      '--accent-warm':'#C8FF00',
      '--orange':     '#FFB000',
    },
  },
  offend: {
    name: 'OFFEND',
    tagline: 'Crimson + acid yellow-green · racing aggression',
    swatch: ['#080403', '#FF1A33', '#CCFF00'],
    vars: {
      '--bg':         '#080403',
      '--surface':    '#140807',
      '--border':     '#3D1010',
      '--primary':    '#FF1A33',
      '--primary-dim':'#FFB3B8',
      '--text-mute':  '#7A4A4A',
      '--accent':     '#CCFF00',
      '--accent-warm':'#FFB000',
      '--orange':     '#FF6B00',
    },
  },
  midnight: {
    name: 'MIDNIGHT',
    tagline: 'Indigo + mint + amber · calm, premium boutique',
    swatch: ['#0A0E1F', '#00FFAA', '#FFB000'],
    vars: {
      '--bg':         '#0A0E1F',
      '--surface':    '#0F142E',
      '--border':     '#1F2540',
      '--primary':    '#00FFAA',
      '--primary-dim':'#A8E0CC',
      '--text-mute':  '#5B6580',
      '--accent':     '#FFB000',
      '--accent-warm':'#FF6B9D',
      '--orange':     '#FF9F0A',
    },
  },
};

export default function DesignPreviewPage() {
  const [active, setActive] = useState('terminal');
  const theme = THEMES[active];

  return (
    <div style={{ ...theme.vars, background: 'var(--bg)', minHeight: '100vh' }} className="font-mono" data-testid="design-preview-page">
      {/* Theme switcher */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', paddingTop: 'env(safe-area-inset-top)' }} className="sticky top-0 z-50">
        <div className="px-4 py-3">
          <p style={{ color: 'var(--primary)' }} className="text-xs uppercase tracking-widest font-bold mb-2">{'>'} DESIGN_LAB · pick a vibe</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(THEMES).map(([key, t]) => {
              const isActive = key === active;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActive(key)}
                  data-testid={`theme-${key}`}
                  style={{
                    background: isActive ? t.vars['--primary'] : 'transparent',
                    color: isActive ? t.vars['--bg'] : t.vars['--primary'],
                    borderColor: t.vars['--primary'],
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  }}
                  className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 transition-all"
                >
                  <span className="flex gap-0.5">
                    {t.swatch.map((c, i) => (
                      <span key={i} style={{ background: c, width: 8, height: 8, display: 'inline-block', border: '1px solid rgba(255,255,255,0.2)' }} />
                    ))}
                  </span>
                  <span className="truncate">{t.name}</span>
                </button>
              );
            })}
          </div>
          <p style={{ color: 'var(--text-mute)' }} className="text-[10px] mt-2 uppercase tracking-wider">// {theme.tagline}</p>
        </div>
      </div>

      {/* Mock customer dashboard */}
      <MockDashboard />
    </div>
  );
}

function MockDashboard() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Greeting */}
      <div className="mb-6 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 style={{ color: 'var(--primary)' }} className="text-xl sm:text-2xl font-bold">{'>'} GM, ALEX <span>☀️</span></h1>
          <p style={{ color: 'var(--primary)' }} className="text-[10px] sm:text-xs mt-1 uppercase tracking-wider animate-pulse">{'//'} ORDER ON THE WAY</p>
        </div>
        <div className="flex gap-2 items-center justify-end flex-wrap">
          <span style={{ borderColor: 'var(--orange)', background: 'rgba(255,159,10,0.06)', color: 'var(--orange)' }} className="px-2 py-1.5 border text-xs flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /><span className="font-bold">800</span>
          </span>
          {[Bell, Gift, Package, User].map((Icon, i) => (
            <button key={i} style={{ borderColor: 'var(--border)', color: 'var(--primary)' }} className="border p-2">
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>

      {/* Active orders strip */}
      <div style={{ borderColor: 'var(--primary)', background: 'rgba(0,0,0,0.3)' }} className="border p-4 mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock style={{ color: 'var(--primary)' }} className="w-5 h-5" />
          <span style={{ color: 'var(--primary)' }} className="font-bold text-sm">3 ACTIVE ORDERS</span>
        </div>
        <button style={{ background: 'var(--primary)', color: 'var(--bg)' }} className="px-4 py-2 text-xs font-bold uppercase tracking-wider">TRACK NOW</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button style={{ background: 'var(--primary)', color: 'var(--bg)' }} className="px-4 py-2 text-xs font-bold uppercase flex items-center gap-2"><ShoppingCart className="w-3 h-3"/>SHOP</button>
        <button style={{ borderColor: 'var(--border)', color: 'var(--primary-dim)' }} className="border px-4 py-2 text-xs font-bold uppercase flex items-center gap-2"><Package className="w-3 h-3"/>RECENT ORDERS</button>
      </div>

      {/* Strain finder banner */}
      <div style={{ borderColor: 'var(--accent)', boxShadow: '0 0 24px rgba(255,61,142,0.15)', background: 'linear-gradient(135deg, rgba(255,61,142,0.08), rgba(255,176,0,0.05))' }} className="border-2 p-4 mb-6 flex items-center gap-4 rounded-2xl">
        <div style={{ background: 'rgba(255,255,255,0.06)' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
          <Sparkles style={{ color: 'var(--accent-warm)' }} className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <p className="text-white font-bold text-base uppercase">Strain Finder</p>
          <p style={{ color: 'var(--primary-dim)' }} className="text-xs mt-0.5">Not sure what to pick? Personalized match in 5 seconds.</p>
        </div>
        <span style={{ color: 'var(--accent)' }} className="font-bold uppercase text-xs tracking-widest">START →</span>
      </div>

      {/* Search */}
      <div style={{ borderColor: 'var(--border)', background: 'var(--surface)' }} className="border flex items-center gap-2 px-3 py-3 mb-3">
        <Search style={{ color: 'var(--text-mute)' }} className="w-4 h-4"/>
        <span style={{ color: 'var(--text-mute)' }} className="text-sm">Search products...</span>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[{l:'ALL', active:true},{l:'FLOWER'},{l:'EDIBLES'},{l:'CONCENTRATES'},{l:'ACCESSORIES'}].map(c => (
          <button key={c.l} style={c.active ? { background: 'var(--primary)', color: 'var(--bg)' } : { borderColor: 'var(--border)', color: 'var(--primary-dim)' }} className={`${c.active ? '' : 'border'} px-3 py-2 text-xs font-bold uppercase`}>{c.l}</button>
        ))}
      </div>

      {/* Product card sample (flower) */}
      <div style={{ borderColor: 'var(--border)', background: 'var(--surface)' }} className="border max-w-xs">
        <div className="aspect-square relative" style={{ background: 'var(--bg)' }}>
          <div className="absolute inset-0 flex items-center justify-center" style={{ color: 'var(--border)' }}>
            <Package className="w-16 h-16" />
          </div>
          <span style={{ background: 'var(--primary)', color: 'var(--bg)' }} className="absolute top-2 right-2 px-2 py-1 text-[10px] font-bold uppercase">SATIVA</span>
          <button style={{ background: 'rgba(0,0,0,0.7)', borderColor: 'var(--border)', color: 'var(--text-mute)' }} className="absolute top-2 left-2 p-2 border"><Heart className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <h3 style={{ color: 'var(--primary)' }} className="font-bold text-sm">Lulu Lemons</h3>
            <p style={{ color: 'var(--text-mute)' }} className="text-xs uppercase">FLOWER</p>
          </div>
          <p style={{ color: 'var(--primary-dim)' }} className="text-xs">Bright citrus burst, sunlight on a focused afternoon.</p>
          <div className="flex flex-wrap gap-1.5">
            {['Uplifted', 'Focused', 'Creative'].map(e => (
              <span key={e} style={{ borderColor: 'var(--border)', color: 'var(--primary-dim)' }} className="border px-2 py-1 text-[10px] uppercase tracking-wider">{e}</span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[{s:'3.5', p:30, a:true},{s:'7',p:45},{s:'1/2',p:70},{s:'O',p:130}].map(o=>(
              <button key={o.s}
                style={o.a
                  ? { borderColor: 'var(--primary)', background: 'rgba(51,255,87,0.08)', color: 'var(--primary)', boxShadow: '0 0 8px rgba(51,255,87,0.25)' }
                  : { borderColor: 'var(--border)', color: 'var(--primary-dim)' }}
                className="border px-3 py-2 text-sm flex justify-between"
              >
                <span className="font-bold">{o.s}</span><span style={{ color: o.a ? 'var(--primary)' : 'var(--text-mute)' }}>${o.p}</span>
              </button>
            ))}
          </div>
          <button style={{ background: 'var(--primary)', color: 'var(--bg)' }} className="w-full py-2 font-bold uppercase flex items-center justify-center gap-2">ADD $30 <Plus className="w-4 h-4"/></button>
        </div>
      </div>

      {/* Footer note */}
      <p style={{ color: 'var(--text-mute)' }} className="text-[10px] uppercase tracking-wider mt-8 text-center">// LAB MODE · ONLY YOU CAN SEE THIS · TAP A THEME ABOVE TO SWAP LIVE</p>
    </div>
  );
}
